import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { CalendarDays, Clock3, Compass, Heart, MapPin, Menu, MoonStar, Phone, Share2, X } from "lucide-react";
import { BookingModal, ReviewModal } from "../components/Modals";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = { name: "Jyoti Sharma - Tarot Card Reader", category: "Tarot reader · Astrologer", rating: 4.8, review_count: 54, status: "Closed", opens: "Opens 9 am Wed", address: "52/120, VT Rd, Ward 27, Mansarovar Sector 5, Mansarovar, Jaipur, Rajasthan 302020", phone: "+91 98765 43210", hero_image: "https://images.unsplash.com/photo-1607773709367-06b7a91f7e4a?auto=format&fit=crop&w=1600&q=85", photos: [] };
const PORTRAIT = "https://images.unsplash.com/photo-1720391593372-30f7909f1684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHx0YXJvdCUyMHJlYWRlciUyMG15c3RpYyUyMHdvbWFuJTIwcG9ydHJhaXQlMjBzcGlyaXR1YWwlMjBndWlkZXxlbnwwfHx8fDE3OTEzMDA2Njh8MA&ixlib=rb-4.1.0&q=85";
const TABLE = "https://images.pexels.com/photos/7179792/pexels-photo-7179792.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const JAIPUR = "https://images.unsplash.com/photo-1582998451055-5ce52763e246?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxqYWlwdXIlMjByYWphc3RoYW4lMjBhcmNoaXRlY3R1cmUlMjBwYWxhY2UlMjBoZXJpdGFnZSUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fDE3OTEzMDA2Njh8MA&ixlib=rb-4.1.0&q=85";
const MARQUEE = ["Tarot Readings", "Birth Chart Astrology", "Numerology", "Relationship Guidance", "Career Insight", "Mansarovar · Jaipur"];
const EASE = [0.22, 1, 0.36, 1];

const maskLine = { hidden: { y: "115%" }, show: (i) => ({ y: "0%", transition: { duration: 1.05, delay: 0.25 + i * 0.14, ease: EASE } }) };
const fadeUp = { hidden: { opacity: 0, y: 26 }, show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.85, delay: 0.55 + i * 0.1, ease: EASE } }) };

const Reveal = ({ children, className, delay = 0 }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 38 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, delay, ease: EASE }}>{children}</motion.div>
);

const stars = (rating) => (
  <span className="stars" aria-label={`${rating} out of 5 stars`}>
    {"★★★★★".split("").map((s, i) => <span key={i} className={i < Math.round(rating) ? "star-on" : "star-off"}>{s}</span>)}
  </span>
);

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" className="logo-mark" aria-hidden="true">
    <rect width="64" height="64" rx="16" fill="#14121a" stroke="rgba(212,175,55,.45)" />
    <path d="M32 12l5.2 14.8L52 32l-14.8 5.2L32 52l-5.2-14.8L12 32l14.8-5.2z" fill="#D4AF37" />
  </svg>
);

export default function Home() {
  const [profile, setProfile] = useState(FALLBACK);
  const [reviews, setReviews] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    Promise.all([axios.get(`${API}/profile`), axios.get(`${API}/reviews`)])
      .then(([p, r]) => { setProfile(p.data); setReviews(r.data); })
      .catch(() => toast.error("Showing the saved profile preview."));
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) lenisRef.current ? lenisRef.current.scrollTo(el, { offset: -72 }) : el.scrollIntoView({ behavior: "smooth" });
  };
  const share = async () => { try { await navigator.clipboard.writeText(window.location.href); toast.success("Profile link copied."); } catch { toast.info("Share this page from your browser menu."); } };
  const photos = profile.photos.length ? profile.photos : [profile.hero_image];

  return (
    <div className="site" id="top" data-testid="business-profile-page">
      <div className="grain" aria-hidden="true" />

      <header className="topbar">
        <button className="brand" data-testid="brand-home-link" onClick={() => scrollTo("top")}>
          <LogoMark />
          <span className="brand-name">Jyoti <em>Sharma</em></span>
        </button>
        <nav className="nav-links">
          <button data-testid="nav-experience-link" onClick={() => scrollTo("experience")}>Experience</button>
          <button data-testid="nav-services-link" onClick={() => scrollTo("services")}>Services</button>
          <button data-testid="nav-reviews-link" onClick={() => scrollTo("reviews")}>Reviews</button>
          <button data-testid="nav-visit-link" onClick={() => scrollTo("visit")}>Visit</button>
        </nav>
        <button className="gold-button sm" data-testid="nav-book-button" onClick={() => setBookingOpen(true)}><CalendarDays size={15} /> Book a reading</button>
        <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">{menuOpen ? <X size={19} /> : <Menu size={19} />}</button>
      </header>
      {menuOpen && (
        <div className="mobile-menu" data-testid="mobile-menu">
          <button data-testid="mobile-overview-button" onClick={() => scrollTo("experience")}>Experience</button>
          <button data-testid="mobile-services-button" onClick={() => scrollTo("services")}>Services</button>
          <button data-testid="mobile-reviews-button" onClick={() => scrollTo("reviews")}>Reviews</button>
          <button data-testid="mobile-visit-button" onClick={() => scrollTo("visit")}>Visit</button>
          <button data-testid="mobile-book-reading-button" onClick={() => { setMenuOpen(false); setBookingOpen(true); }}>Book a reading</button>
        </div>
      )}

      <main>
        <section className="hero" ref={heroRef}>
          <div className="hero-copy">
            <motion.p className="eyebrow" variants={fadeUp} custom={0} initial="hidden" animate="show"><span className="rule" />Mansarovar · Jaipur</motion.p>
            <h1 data-testid="business-name">
              <span className="mask"><motion.span variants={maskLine} custom={0} initial="hidden" animate="show">Jyoti Sharma</motion.span></span>
              <span className="mask"><motion.span variants={maskLine} custom={1} initial="hidden" animate="show"><em>Tarot Card Reader</em></motion.span></span>
            </h1>
            <motion.div className="rating-row" data-testid="business-rating" variants={fadeUp} custom={1} initial="hidden" animate="show">
              {stars(profile.rating)} <b>{profile.rating}</b><span>({profile.review_count} reviews)</span><i>✦</i><span>Astrologer</span>
            </motion.div>
            <motion.p className="intro" variants={fadeUp} custom={2} initial="hidden" animate="show">
              A quiet space for honest insight, intuitive guidance, and the questions you’ve been carrying.
            </motion.p>
            <motion.div className="hero-actions" variants={fadeUp} custom={3} initial="hidden" animate="show">
              <button className="gold-button" data-testid="book-reading-button" onClick={() => setBookingOpen(true)}><CalendarDays size={16} /> Book a reading</button>
              <button className="ghost-button" data-testid="contact-button" onClick={() => (window.location.href = `tel:${profile.phone}`)}><Phone size={15} /> {profile.phone}</button>
            </motion.div>
            <motion.div className="action-links" variants={fadeUp} custom={4} initial="hidden" animate="show">
              <button data-testid="directions-button" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(profile.address)}`, "_blank")}><Compass size={15} /> Directions</button>
              <button data-testid="share-button" onClick={share}><Share2 size={15} /> Share</button>
              <button className={saved ? "saved" : ""} data-testid="save-button" onClick={() => { setSaved(!saved); toast.success(saved ? "Removed from saved places." : "Saved to your places."); }}><Heart size={15} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}</button>
            </motion.div>
          </div>
          <motion.div className="hero-visual" initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.5, delay: 0.35, ease: EASE }}>
            <div className="hero-frame">
              <motion.img src={profile.hero_image} alt="Tarot cards spread by candlelight in Jyoti Sharma's reading room" data-testid="hero-image" style={{ y: imgY }} />
            </div>
            <motion.div className="hero-badge" style={{ y: badgeY }}>
              <MoonStar size={17} />
              <div><b>By appointment</b><small>{profile.status} · {profile.opens}</small></div>
            </motion.div>
          </motion.div>
        </section>

        <div className="marquee" aria-hidden="true" data-testid="services-marquee">
          <div className="marquee-track">
            {[0, 1].map((k) => (
              <div className="marquee-group" key={k}>{MARQUEE.map((t) => <span key={t}>{t}<i>✦</i></span>)}</div>
            ))}
          </div>
        </div>

        <section className="section experience" id="experience">
          <Reveal className="experience-copy">
            <span className="section-kicker">The Experience</span>
            <h2>Come with a question.<br /><em>Leave with clarity.</em></h2>
            <p>Jyoti offers warm, grounded readings that blend tarot with astrology and numerology. Each session is a private pause to listen inward, see patterns, and move forward with intention.</p>
            <p>Evenings by candlelight, a cup of chai, and an honest conversation about where you are — and where you might go next.</p>
          </Reveal>
          <Reveal className="experience-media" delay={0.15}>
            <img src={TABLE} alt="A reading table set with tarot cards, candles and crystals" loading="lazy" />
            <span className="media-caption"><i>✦</i> The reading table, Mansarovar</span>
          </Reveal>
        </section>

        <section className="section" id="services">
          <Reveal className="section-head">
            <span className="section-kicker">Services</span>
            <h2>Three ways <em>in.</em></h2>
          </Reveal>
          <div className="bento">
            <Reveal className="bento-card area-a" delay={0}>
              <span className="bento-num">01</span>
              <h3>Tarot Reading</h3>
              <p>Insight and direction for love, career, and the crossroads you cannot name yet.</p>
              <button className="card-link" data-testid="service-tarot-book" onClick={() => setBookingOpen(true)}>Book this session →</button>
            </Reveal>
            <Reveal className="bento-card area-b" delay={0.08}>
              <span className="bento-num">02</span>
              <h3>Birth Chart</h3>
              <p>Your cosmic map — planets, houses and the timing of things.</p>
            </Reveal>
            <Reveal className="bento-card area-c status" delay={0.16}>
              <div className="status-top"><span className="status-dot" /><b>{profile.status}</b></div>
              <p>{profile.opens}</p>
              <small>Hours may vary. Appointments are recommended.</small>
              <Clock3 size={20} className="status-icon" />
            </Reveal>
            <Reveal className="bento-card area-d" delay={0.24}>
              <span className="bento-num">03</span>
              <h3>Numerology</h3>
              <p>The patterns hidden in your numbers, read with care.</p>
            </Reveal>
            <Reveal className="bento-card area-e quote" delay={0.32}>
              <p>“My role is not to tell you what to do. It is to help you hear what you already know.”</p>
              <div className="quote-foot">{stars(profile.rating)}<b>{profile.rating}</b><span>· {profile.review_count} community reviews</span></div>
            </Reveal>
          </div>
        </section>

        <section className="section owner" id="owner">
          <Reveal className="owner-media">
            <img src={PORTRAIT} alt="Jyoti Sharma, tarot card reader and astrologer" loading="lazy" />
          </Reveal>
          <Reveal className="owner-copy" delay={0.15}>
            <span className="section-kicker">A Note From Jyoti</span>
            <h2>Your story already <em>knows the way.</em></h2>
            <p className="owner-quote">“Every reading begins with listening. The cards only open the door — you walk through it yourself, with a little more trust than you came in with.”</p>
            <b className="owner-sign">— Jyoti Sharma</b>
          </Reveal>
        </section>

        <section className="section reviews" id="reviews">
          <Reveal className="reviews-head">
            <div>
              <span className="section-kicker">Community Notes</span>
              <h2>What people <em>are saying.</em></h2>
            </div>
            <button className="gold-button" data-testid="write-review-button" onClick={() => setReviewOpen(true)}>Write a review</button>
          </Reveal>
          <div className="reviews-list">
            {reviews.map((item, idx) => (
              <Reveal className="review-item" key={item.id} delay={Math.min(idx * 0.06, 0.3)}>
                <article data-testid="review-item" className="review-inner">
                  <div className="review-avatar">{item.name[0]}</div>
                  <div>
                    <div className="review-meta"><b>{item.name}</b>{stars(item.rating)}<span>{item.created_at.slice(0, 10)}</span></div>
                    <p>{item.text}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section photos" id="photos">
          <Reveal className="section-head">
            <span className="section-kicker">The Space</span>
            <h2>Where the <em>cards open.</em></h2>
          </Reveal>
          <div className="photos-grid">
            {photos.slice(0, 3).map((photo, i) => (
              <Reveal className={`photo-frame f${i + 1}`} key={photo} delay={i * 0.1}>
                <img src={photo} alt={`Jyoti Sharma's tarot reading space ${i + 1}`} data-testid={`photo-${i + 1}`} loading="lazy" />
              </Reveal>
            ))}
          </div>
        </section>

        <section className="section visit" id="visit">
          <Reveal className="visit-copy">
            <span className="section-kicker">Visit</span>
            <h2>In the heart of <em>Mansarovar.</em></h2>
            <div className="address" data-testid="location-card">
              <MapPin size={18} />
              <span>{profile.address}</span>
            </div>
            <div className="visit-actions">
              <button className="gold-button" data-testid="map-directions-button" onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(profile.address)}`, "_blank")}><Compass size={15} /> Get directions</button>
              <button className="ghost-button" data-testid="visit-call-button" onClick={() => (window.location.href = `tel:${profile.phone}`)}><Phone size={15} /> Call</button>
            </div>
            <img className="visit-photo" src={JAIPUR} alt="Jaipur's heritage architecture at golden hour" loading="lazy" />
          </Reveal>
          <Reveal className="map-card" delay={0.15}>
            <div className="map-art">
              <div className="map-grid" />
              <span className="map-pin"><MapPin size={24} fill="currentColor" /></span>
              <span className="map-label">Mansarovar</span>
            </div>
            <div className="map-foot"><b>Sector 5, Mansarovar</b><small>Jaipur, Rajasthan 302020</small></div>
          </Reveal>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-brand"><LogoMark size={24} /><span>Jyoti <em>Sharma</em></span></div>
        <p>Tarot · Astrology · Numerology — Mansarovar, Jaipur</p>
        <a href="/admin" className="admin-link" data-testid="admin-footer-link">Owner login</a>
      </footer>

      <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
      <ReviewModal open={reviewOpen} onClose={() => setReviewOpen(false)} onPublished={(r) => setReviews([r, ...reviews])} />
    </div>
  );
}
